import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import ProtectedRoute from "./protectedRoutes";
import Layout from "../components/layout/layout";
import { LoadingPage } from "../pages/common/loading";

const Login = lazy(() =>
  import("../pages/auth/login").then((module) => ({ default: module.Login })),
);
const LoginSupportUser = lazy(() =>
  import("../pages/auth/loginSupportUser").then((module) => ({ default: module.LoginSupportUser })),
);
const RecoverPassword = lazy(() =>
  import("../pages/auth/recoverPassword").then((module) => ({ default: module.RecoverPassword })),
);
const ChangePasswordFT = lazy(() =>
  import("../pages/auth/changePasswordFT").then((module) => ({ default: module.ChangePasswordFT })),
);
const ValidateCredentials = lazy(() =>
  import("../pages/auth/validateCredentials").then((module) => ({
    default: module.ValidateCredentials,
  })),
);
const AdminHome = lazy(() =>
  import("../pages/admin/home").then((module) => ({ default: module.AdminHome })),
);
const ListUsers = lazy(() =>
  import("../pages/admin/listUsers").then((module) => ({ default: module.ListUsers })),
);
const UserDetail = lazy(() =>
  import("../pages/admin/userDetail").then((module) => ({ default: module.UserDetail })),
);
const ModalCreateOptions = lazy(() => import("../components/common/modalCreateOptions"));
const ModalEditOptions = lazy(() => import("../components/common/modalEditOptions"));
const ListMenuOptions = lazy(() =>
  import("../pages/admin/listMenuOptions").then((module) => ({ default: module.ListMenuOptions })),
);
const Dashboard = lazy(() => import("../pages/admin/dashboard"));
const NotFound = lazy(() => import("../pages/common/notfound"));
const ListRoles = lazy(() =>
  import("../pages/admin/listRoles").then((module) => ({ default: module.ListRoles })),
);
const HomeNewCharts = lazy(() =>
  import("../pages/user/homeNewCharts").then((module) => ({ default: module.HomeNewCharts })),
);
const BillFiscalCEN = lazy(() =>
  import("../pages/user/fiscalCEN/billFiscalCEN").then((module) => ({
    default: module.BillFiscalCEN,
  })),
);
const PayrollFiscalCEN = lazy(() =>
  import("../pages/user/fiscalCEN/payrollFiscalCEN").then((module) => ({
    default: module.PayrollFiscalCEN,
  })),
);
const AdminGeographies = lazy(() => import("../pages/admin/geographies/adminGeographies"));
const CreateGeography = lazy(() => import("../pages/admin/geographies/CRUD/createGeography"));
const SalesForce = lazy(() =>
  import("../pages/user/salesForce/salesForce").then((module) => ({ default: module.SalesForce })),
);
const CatalogueUnified = lazy(() => import("../pages/user/catalogue/catalogueUnified"));
const AlarmUnified = lazy(() => import("../pages/user/alarms/alarmsUnified"));
const DynamicsUnified = lazy(() => import("../pages/user/businessDynamics/dynamicsUnified"));
const SalesIndicators = lazy(() => import("../pages/user/accessSalesIndicators/salesIndicators"));
const StoreSalesIndicators = lazy(
  () => import("../pages/user/storeIndicators/storeSalesIndicators"),
);
const StoreStockIndicators = lazy(
  () => import("../pages/user/storeIndicators/storeStockIndicators"),
);
const Stores = lazy(() =>
  import("../pages/user/stores/stores").then((module) => ({ default: module.Stores })),
);
const Masters = lazy(() =>
  import("../pages/user/masters/masters").then((module) => ({ default: module.Masters })),
);
const CreateDynamicPeriodicReport = lazy(() =>
  import("../pages/user/dynamicPeriodicReport/createDynamicPeriodicReport").then((module) => ({
    default: module.CreateDynamicPeriodicReport,
  })),
);
const CreateDynamicPeriodicReportDistributor = lazy(() =>
  import("../pages/user/dynamicPeriodicReportDistributor/createDynamicPeriodicReportDistributor").then(
    (module) => ({ default: module.CreateDynamicPeriodicReportDistributor }),
  ),
);
const ListDynamicPeriodicReports = lazy(() =>
  import("../pages/user/dynamicPeriodicReport/listDynamicPeriodicReports").then((module) => ({
    default: module.ListDynamicPeriodicReports,
  })),
);
const ListDynamicPeriodicReportsDistributor = lazy(() =>
  import("../pages/user/dynamicPeriodicReportDistributor/listDynamicPeriodicReportsDistributor").then(
    (module) => ({ default: module.ListDynamicPeriodicReportsDistributor }),
  ),
);
const BusinessAlarmManager = lazy(() =>
  import("../pages/user/managingBusinesAlarm/BusinessAlarmManager").then((module) => ({
    default: module.BusinessAlarmManager,
  })),
);
const BusinessDynamicsRequests = lazy(
  () => import("../pages/user/businessDynamics/requests/businessDynamicsRequests"),
);
const ManageBusinessDynamicsRequests = lazy(
  () => import("../pages/user/businessDynamics/manage/manageBusinessDynamicsRequests"),
);
const InventoriesIndicators = lazy(
  () => import("../pages/user/inventoriesIndicators/inventoriesIndicators"),
);
const AccessReturnsIndicators = lazy(
  () => import("../pages/user/inventoriesIndicators/returnsIndicators"),
);
const AccessBudgetIndicators = lazy(
  () => import("../pages/user/inventoriesIndicators/budgetIndicators"),
);
const AdminNotifications = lazy(() => import("../pages/user/notifications/adminNotifications"));
const AdminResourceManagement = lazy(
  () => import("../pages/user/resourceManagement/adminResourceManagement"),
);
const DeleteAndReload = lazy(() => import("../pages/user/deleteAndReload/deleteAndReload"));
const EditDynamicPeriodicReport = lazy(() =>
  import("../pages/user/dynamicPeriodicReport/editDynamicPeriodicReport").then((module) => ({
    default: module.EditDynamicPeriodicReport,
  })),
);
const EditDynamicPeriodicReportDistributor = lazy(() =>
  import("../pages/user/dynamicPeriodicReportDistributor/editDynamicPeriodicReportDistributor").then(
    (module) => ({ default: module.EditDynamicPeriodicReportDistributor }),
  ),
);
const ManageCompanyLogo = lazy(() => import("../pages/user/manageCompanyLogo/manageCompanyLogo"));
const ApplicationDeleteAndReload = lazy(
  () => import("../pages/user/deleteAndReload/applicationDeleteAndReload"),
);
const AdminCompanies = lazy(() => import("../pages/admin/adminCompanies"));
const AdminBusinessRules = lazy(() => import("../pages/user/businessRules/adminBusinessRules"));
const AdminIndicators = lazy(() => import("../pages/user/indicators/adminIndicators"));
const AdminSpecialFilesClient = lazy(() => import("../pages/user/specialFiles/adminSpecialFiles"));
const CreateSpecialFileClient = lazy(() => import("../pages/user/specialFiles/createSpecialFile"));
const AdminSpecialFilesClientDistributors = lazy(
  () => import("../pages/user/specialFilesDistributors/adminSpecialFilesDistributors"),
);
const CreateSpecialFileDistributor = lazy(
  () => import("../pages/user/specialFilesDistributors/createSpecialFileDistributor"),
);
const EditSpecialFileClientDistributor = lazy(
  () => import("../pages/user/specialFilesDistributors/editSpecialFileDistributor"),
);
const AdminSpecialFilesAdmin = lazy(() => import("../pages/admin/adminSpecialFiles"));
const EditSpecialFileClient = lazy(() => import("../pages/user/specialFiles/editSpecialFile"));
const ConsultRoles = lazy(() => import("../pages/user/adminUsers/consultRoles"));
const ManagingExternalUsers = lazy(() =>
  import("../pages/user/managePortalUsers/managingExternalUsers").then((module) => ({
    default: module.ManagingExternalUsers,
  })),
);
const AdminClientUsers = lazy(() => import("../pages/user/adminUsers/adminClientUsers"));
const AdminIndicatorCards = lazy(() => import("../pages/admin/adminIndicatorCards"));
const AdminContacts = lazy(() => import("../pages/user/contacts/adminContacts"));
const AdminExceptionOC = lazy(() => import("../pages/user/exceptionManagerOC/adminExceptionOC"));
const AdminExceptionCommerceOC = lazy(
  () => import("../pages/user/exceptionManagerOCbyCommerce/adminExceptionCommerceOC"),
);
const AdminTransmittedValue = lazy(() => import("../pages/admin/adminTransmittedValue"));
const ManageBusinessGroup = lazy(
  () => import("../pages/admin/manageBusinessGroup/manageBusinessGroup"),
);
const AdminSendAllies = lazy(() => import("../pages/admin/adminSendAllies"));
const ReloadAllies = lazy(() => import("../pages/user/allies/reloadAllies"));
const RequestAllies = lazy(() => import("../pages/user/allies/requestAllies"));
const BillIndicators = lazy(() => import("../pages/user/inventoriesIndicators/billIndicators"));
const PayrollIndicators = lazy(
  () => import("../pages/user/inventoriesIndicators/payrollIndicators"),
);
const RateMaster = lazy(() => import("../pages/user/rateMaster/rateMaster"));
const UnifiedCatalogTraders = lazy(() => import("../pages/user/traders/catalog/unifiedCatalog"));
const UnifiedMastersTraders = lazy(() => import("../pages/user/traders/masters/unifiedMasters"));
const FillRateIndicator = lazy(
  () => import("../pages/user/inventoriesIndicators/fillRateIndicator"),
);
const RequestsPurchaseOrders = lazy(
  () => import("../pages/user/purchaseOrders/requestsPurchaseOrders"),
);
const AdminPurchaseOrders = lazy(() => import("../pages/user/purchaseOrders/adminPurchaseOrders"));
const AdminPurchaseOrderCommerce = lazy(
  () => import("../pages/user/purchaseOrders/adminPurchaseOrderCommerce"),
);
const ExternalFiles = lazy(() => import("../pages/user/externalFiles/externalFiles"));
const FieldLoadMapping = lazy(() => import("../pages/user/loadMapping/FieldLoadMapping"));
const TraceabilityExternal = lazy(
  () => import("../pages/user/traceabilityExternal/traceabilityExternal"),
);
const EmissionCENFiscalHome = lazy(() =>
  import("../pages/user/cenFiscalCO/emission/emissionCENFiscalHome").then((module) => ({
    default: module.EmissionCENFiscalHome,
  })),
);
const ReceptionCENFiscalHome = lazy(() =>
  import("../pages/user/cenFiscalCO/reception/receptionCENFiscalHome").then((module) => ({
    default: module.ReceptionCENFiscalHome,
  })),
);
const EmissionIndicators = lazy(
  () => import("../pages/user/cenFiscalCO/emission/emissionIndicators"),
);
const ReceptionIndicators = lazy(
  () => import("../pages/user/cenFiscalCO/reception/receptionIndicators"),
);
const HelpCenter = lazy(() => import("../pages/common/helpCenter"));
const AdminReprocess = lazy(() => import("../pages/user/reprocess/adminReprocess"));
const ListPeriodicReportCEN = lazy(
  () => import("../pages/user/periodicReportCEN/listPeriodicReportCEN"),
);
const CreatePeriodicReportCEN = lazy(
  () => import("../pages/user/periodicReportCEN/createPeriodicReportCEN"),
);
const EditPeriodicReportCEN = lazy(
  () => import("../pages/user/periodicReportCEN/editPeriodicReportCEN"),
);
const RequestBILayers = lazy(() => import("../pages/user/BILayer/requestBILayer"));
const BILayers = lazy(() =>
  import("../pages/user/BILayer/BILayers").then((module) => ({ default: module.BILayers })),
);
const UnifiedWhatIf = lazy(() => import("../pages/user/whatIf/unifiedWhatIf"));
const ListSimulations = lazy(() => import("../pages/user/whatIf/seeSimulations/listSimulations"));
const ActionalIndicators = lazy(
  () => import("../pages/user/aditionalIndicators/ActionalIndicators"),
);
const RadarCommerceIndicators = lazy(
  () => import("../pages/user/aditionalIndicators/RadarCommerceIndicators"),
);
const AnaliticaShopperIndicators = lazy(
  () => import("../pages/user/aditionalIndicators/AnaliticaShopperIndicators"),
);
const UsabilityIndicators = lazy(
  () => import("../pages/user/aditionalIndicators/UsabilitIndicators"),
);
const ReturnAlarmsIndicators = lazy(
  () => import("../pages/user/aditionalIndicators/ReturnAlarmsIndicadors"),
);
const CortexMastersPage = lazy(() => import("../pages/cortexMasters/pages/CortexMastersPage"));

/** Define las rutas de la aplicación con lazy loading y protección por roles. */
function AppRoutes() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/support" element={<LoginSupportUser />} />
        <Route path="/recoverPassword" element={<RecoverPassword />} />
        <Route path="/changePasswordFT" element={<ChangePasswordFT />} />
        <Route path="/validateCredentials/email/:email" element={<ValidateCredentials />} />
        {/* Modales */}
        <Route path="/modalCreateOptions" element={<ModalCreateOptions />} />
        <Route path="/modalEditOptions" element={<ModalEditOptions />} />
        {/** Help Center */}
        <Route path="user" element={<Layout />}>
          <Route path="helpCenter" element={<HelpCenter />} />
        </Route>
        <Route path="admin" element={<Layout />}>
          <Route path="helpCenter" element={<HelpCenter />} />
        </Route>
        {/* Admin Routes */}
        <Route element={<ProtectedRoute requiredRole={"admin"} />}>
          {/* <Route path="/support" element={<LoginSupportUser />} /> */}

          <Route path="admin" element={<Layout />}>
            <Route path="home" element={<AdminHome />} />
            <Route path="helpCenter" element={<HelpCenter />} />

            <Route path="listUsers" element={<ListUsers />} />
            <Route path="listUsers/:country/:companyId" element={<UserDetail />} />
            <Route path="listMenuOptions" element={<ListMenuOptions />} />
            <Route path="listRoles" element={<ListRoles />} />
            <Route path="adminGeographies" element={<AdminGeographies />} />
            <Route path="uploadGeography" element={<CreateGeography />} />
            <Route path="adminCompanies" element={<AdminCompanies />} />
            <Route path="stores" element={<Stores />} />
            <Route path="loadMapping" element={<FieldLoadMapping />} />
            <Route path="adminSpecialFiles" element={<AdminSpecialFilesAdmin />} />
            <Route path="adminSpecialFilesClient" element={<AdminSpecialFilesClient />} />

            <Route path="deleteAndReload" element={<DeleteAndReload />} />
            <Route path="deleteAndReload/request" element={<ApplicationDeleteAndReload />} />
            <Route path="adminIndicatorCards" element={<AdminIndicatorCards />} />
            <Route path="adminTransmittedValue" element={<AdminTransmittedValue />} />
            <Route path="adminBusinessRules" element={<AdminBusinessRules />} />
            <Route path="adminSendAllies" element={<AdminSendAllies />} />
            <Route path="manageBusinessGroup" element={<ManageBusinessGroup />} />
            <Route path="adminIndicators" element={<AdminIndicators />} />
            <Route path="adminNotifications" element={<AdminNotifications />} />
            <Route path="adminResourceManagement" element={<AdminResourceManagement />} />
            <Route path="reloadAllies" element={<ReloadAllies />} />
            <Route path="reloadAllies/request" element={<RequestAllies />} />
            <Route path="adminReprocess" element={<AdminReprocess />} />
            <Route path="UsabilityIndicators" element={<UsabilityIndicators />} />
            <Route path="BILayers/request" element={<RequestBILayers />} />
            <Route path="BILayers" element={<BILayers />} />
          </Route>
        </Route>
        {/* AdminCTYS Routes */}
        {/* User Route home */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="home" />}>
          <Route path="user" element={<Layout />}>
            <Route path="home" element={<HomeNewCharts />} />
          </Route>
        </Route>
        {/* User Route home CEN Bill */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="homeBillCEN" />}>
          <Route path="user" element={<Layout />}>
            <Route path="homeBillCEN" element={<BillFiscalCEN />} />
          </Route>
        </Route>
        {/* User Route home CEN Payroll */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="homePayrollCEN" />}>
          <Route path="user" element={<Layout />}>
            <Route path="homePayrollCEN" element={<PayrollFiscalCEN />} />
          </Route>
        </Route>
        {/* User Route home CEN Emission */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="homeEmissionCEN" />}>
          <Route path="user" element={<Layout />}>
            <Route path="homeEmissionCEN" element={<EmissionCENFiscalHome />} />
          </Route>
        </Route>
        <Route element={<ProtectedRoute requiredRole={"user"} path="emissionIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="emissionIndicators" element={<EmissionIndicators />} />
          </Route>
        </Route>
        {/* User Route home CEN Reception */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="homeReceptionCEN" />}>
          <Route path="user" element={<Layout />}>
            <Route path="homeReceptionCEN" element={<ReceptionCENFiscalHome />} />
          </Route>
        </Route>
        <Route element={<ProtectedRoute requiredRole={"user"} path="receptionIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="receptionIndicators" element={<ReceptionIndicators />} />
          </Route>
        </Route>
        {/* User Route catalog */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="catalog" />}>
          <Route path="user" element={<Layout />}>
            <Route path="catalog" element={<CatalogueUnified />} />
          </Route>
        </Route>
        {/* User Route alarms */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="alarm" />}>
          <Route path="user" element={<Layout />}>
            <Route path="alarms" element={<AlarmUnified />} />
          </Route>
        </Route>
        {/* User Route dinámicas comerciales */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="dynamics" />}>
          <Route path="user" element={<Layout />}>
            <Route path="dynamics" element={<DynamicsUnified />} />
          </Route>
        </Route>
        {/* User Route SalesForce */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="salesForce" />}>
          <Route path="user" element={<Layout />}>
            <Route path="salesForce" element={<SalesForce />} />
          </Route>
        </Route>
        {/* User Route masters */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="masters" />}>
          <Route path="user" element={<Layout />}>
            <Route path="masters" element={<Masters />} />
          </Route>
        </Route>
        {/* User Route cortex masters */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="cortexMasters" />}>
          <Route path="user" element={<Layout />}>
            <Route path="cortexMasters" element={<CortexMastersPage />} />
          </Route>
        </Route>
        {/* User Route delete and reload */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="deleteAndReload" />}>
          <Route path="user" element={<Layout />}>
            <Route path="deleteAndReload" element={<DeleteAndReload />} />
            <Route path="deleteAndReload/request" element={<ApplicationDeleteAndReload />} />
          </Route>
        </Route>
        <Route
          element={
            <ProtectedRoute requiredRole={"user"} path="listDynamicPeriodicReportsDistributor" />
          }
        >
          <Route path="user" element={<Layout />}>
            <Route
              path="listDynamicPeriodicReportsDistributor"
              element={<ListDynamicPeriodicReportsDistributor />}
            />
            <Route
              path="createDynamicPeriodicReportDistributor"
              element={<CreateDynamicPeriodicReportDistributor />}
            />
            <Route
              path="editDynamicPeriodicReportDistributor/:pk/:sk"
              element={<EditDynamicPeriodicReportDistributor />}
            />
          </Route>
        </Route>
        {/* User Route dynamic report */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="listDynamicPeriodicReports" />}>
          <Route path="user" element={<Layout />}>
            <Route path="listDynamicPeriodicReports" element={<ListDynamicPeriodicReports />} />
            <Route path="createDynamicPeriodicReport" element={<CreateDynamicPeriodicReport />} />
            <Route
              path="editDynamicPeriodicReport/:pk/:sk"
              element={<EditDynamicPeriodicReport />}
            />
          </Route>
        </Route>
        {/* User Route Periodic Report CEN */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="listPeriodicReportCEN" />}>
          <Route path="user" element={<Layout />}>
            <Route path="listPeriodicReportCEN/:type" element={<ListPeriodicReportCEN />} />
            <Route path="createPeriodicReportCEN/:type" element={<CreatePeriodicReportCEN />} />
            <Route path="editPeriodicReportCEN/:type/:pk/:sk" element={<EditPeriodicReportCEN />} />
          </Route>
        </Route>
        {/* User Route dashboard */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="dashboard" />}>
          <Route path="user" element={<Layout />}>
            <Route path="dashboard" element={<Dashboard />} />
          </Route>
        </Route>
        {/* User Route salesIndicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="salesIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="salesIndicators" element={<SalesIndicators />} />
          </Route>
        </Route>
        {/* User Route actionalIndicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="actionableInsights" />}>
          <Route path="user" element={<Layout />}>
            <Route path="actionableInsights" element={<ActionalIndicators />} />
          </Route>
        </Route>
        {/* User Route Radar Commerce */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="radarCommerce" />}>
          <Route path="user" element={<Layout />}>
            <Route path="radarCommerce" element={<RadarCommerceIndicators />} />
          </Route>
        </Route>
        {/* User Route AnaliticaShopperIndicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="analiticaShopperIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="analiticaShopperIndicators" element={<AnaliticaShopperIndicators />} />
          </Route>
        </Route>
        {/* User Route ReturnAlarmsIndicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="returnAlarmsIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="returnAlarmsIndicators" element={<ReturnAlarmsIndicators />} />
          </Route>
        </Route>
        {/* User Route UsabilityIndicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="UsabilityIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="UsabilityIndicators" element={<UsabilityIndicators />} />
          </Route>
        </Route>
        {/* User Route storeSalesIndicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="storeSalesIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="storeSalesIndicators" element={<StoreSalesIndicators />} />
          </Route>
        </Route>
        {/* User Route storeStockIndicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="storeStockIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="storeStockIndicators" element={<StoreStockIndicators />} />
          </Route>
        </Route>
        {/* User Route FillRateIndicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="fillRateIndicator" />}>
          <Route path="user" element={<Layout />}>
            <Route path="fillRateIndicator" element={<FillRateIndicator />} />
          </Route>
        </Route>
        {/* User Route inventoriesIndicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="inventoriesIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="inventoriesIndicators" element={<InventoriesIndicators />} />
          </Route>
        </Route>
        {/** User Route returnIndicadors */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="returnsIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="returnsIndicators" element={<AccessReturnsIndicators />} />
          </Route>
        </Route>
        {/* User Route budgetIndicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="budgetIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="budgetIndicators" element={<AccessBudgetIndicators />} />
          </Route>
        </Route>
        {/* User Route listUsers */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="listUsers" />}>
          <Route path="user" element={<Layout />}>
            <Route path="listUsers" element={<ListUsers />} />
            <Route path="listUsers/:country/:companyId" element={<UserDetail />} />
          </Route>
        </Route>
        {/**User Route adminCompanies */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="adminCompanies" />}>
          <Route path="user" element={<Layout />}>
            <Route path="adminCompanies" element={<AdminCompanies />} />
          </Route>
        </Route>
        {/**User Route businessRules */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="adminBusinessRules" />}>
          <Route path="user" element={<Layout />}>
            <Route path="adminBusinessRules" element={<AdminBusinessRules />} />
          </Route>
        </Route>
        {/* User Route stores */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="stores" />}>
          <Route path="user" element={<Layout />}>
            <Route path="stores" element={<Stores />} />
          </Route>
        </Route>
        {/* User Route Allies information */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="infoAllies" />}>
          <Route path="user" element={<Layout />}>
            <Route path="infoAllies" element={<NotFound />} />
          </Route>
        </Route>
        {/* User Route Allies reload */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="reloadAllies" />}>
          <Route path="user" element={<Layout />}>
            <Route path="reloadAllies" element={<ReloadAllies />} />
            <Route path="reloadAllies/request" element={<RequestAllies />} />
          </Route>
        </Route>
        {/* User insight access */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="adminIndicatorCards" />}>
          <Route path="user" element={<Layout />}>
            <Route path="adminIndicatorCards" element={<AdminIndicatorCards />} />
          </Route>
        </Route>
        {/* User External Managing */}
        <Route path="user" element={<Layout />}>
          <Route path="managePortalUsers" element={<ManagingExternalUsers />} />
        </Route>
        {/** Admin Indicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="adminIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="adminIndicators" element={<AdminIndicators />} />{" "}
          </Route>
        </Route>
        {/* User admin transmitted value */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="adminTransmittedValue" />}>
          <Route path="user" element={<Layout />}>
            <Route path="adminTransmittedValue" element={<AdminTransmittedValue />} />
          </Route>
        </Route>
        {/* User admin purchase order and requests */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="adminPurchaseOrder" />}>
          <Route path="user" element={<Layout />}>
            <Route path="adminPurchaseOrder" element={<AdminPurchaseOrders />} />
            <Route path="adminPurchaseOrder/request" element={<RequestsPurchaseOrders />} />
          </Route>
        </Route>
        {/* User admin purchase order commerce */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="adminPurchaseOrderCommerce" />}>
          <Route path="user" element={<Layout />}>
            <Route path="adminPurchaseOrderCommerce" element={<AdminPurchaseOrderCommerce />} />
          </Route>
        </Route>
        {/** User Route Special Files */}
        <Route
          element={<ProtectedRoute requiredRole={"user"} path="adminSpecialFilesDistributors" />}
        >
          <Route path="user" element={<Layout />}>
            <Route
              path="adminSpecialFilesDistributors"
              element={<AdminSpecialFilesClientDistributors />}
            />
            <Route path="createSpecialFileDistributor" element={<CreateSpecialFileDistributor />} />
            <Route
              path="editSpecialFileDistributor/:pk/:sk"
              element={<EditSpecialFileClientDistributor />}
            />
          </Route>
        </Route>
        <Route element={<ProtectedRoute requiredRole={"user"} path="configureSpecialFiles" />}>
          <Route path="user" element={<Layout />}>
            <Route path="configureSpecialFiles" element={<AdminSpecialFilesAdmin />} />
          </Route>
        </Route>
        {/* <Route
        element={
          <ProtectedRoute requiredRole={"user"} path="adminSpecialFiles" />
        }
      > */}
        <Route path="user" element={<Layout />}>
          <Route path="adminSpecialFiles" element={<AdminSpecialFilesClient />} />
          <Route path="createSpecialFiles" element={<CreateSpecialFileClient />} />
          <Route path="editSpecialFiles/:pk/:sk" element={<EditSpecialFileClient />} />
        </Route>
        {/* </Route> */}
        {/** User admin users client */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="adminUsers" />}>
          <Route path="user" element={<Layout />}>
            <Route path="adminUsers" element={<AdminClientUsers />} />
            <Route path="consultRoles" element={<ConsultRoles />} />
          </Route>
        </Route>
        {/** User admin contacts */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="adminContacts" />}>
          <Route path="user" element={<Layout />}>
            <Route path="adminContacts" element={<AdminContacts />} />
          </Route>
        </Route>
        {/** User admin admin Exception OC */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="adminExceptionOC" />}>
          <Route path="user" element={<Layout />}>
            <Route path="adminExceptionOC" element={<AdminExceptionOC />} />
          </Route>
        </Route>
        {/** User admin admin Exception OC */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="adminExceptionOcCommerce" />}>
          <Route path="user" element={<Layout />}>
            <Route path="adminExceptionOcCommerce" element={<AdminExceptionCommerceOC />} />
          </Route>
        </Route>
        {/* Manage Business Group */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="manageBusinessGroup" />}>
          <Route path="user" element={<Layout />}>
            <Route path="manageBusinessGroup" element={<ManageBusinessGroup />} />
          </Route>
        </Route>
        {/** Admin Indicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="adminNotifications" />}>
          <Route path="user" element={<Layout />}>
            <Route path="adminNotifications" element={<AdminNotifications />} />
          </Route>
        </Route>
        {/** Traders  */}
        {/** Products catalog */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="traders/catalog" />}>
          <Route path="user" element={<Layout />}>
            <Route path="traders/catalog" element={<UnifiedCatalogTraders />} />
          </Route>
        </Route>
        <Route element={<ProtectedRoute requiredRole={"user"} path="traders/masters" />}>
          <Route path="user" element={<Layout />}>
            <Route path="traders/masters" element={<UnifiedMastersTraders />} />
          </Route>
        </Route>
        {/** Load mapping */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="loadMapping" />}>
          <Route path="user" element={<Layout />}>
            <Route path="loadMapping" element={<FieldLoadMapping />} />
          </Route>
        </Route>
        {/* User Route delete and reload */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="traceExternal" />}>
          <Route path="user" element={<Layout />}>
            <Route path="user" element={<DeleteAndReload />} />
            <Route path="traceExternal" element={<TraceabilityExternal />} />
          </Route>
        </Route>
        {/* CEN Fiscal */}
        {/* Bill Indicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="billIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="billIndicators" element={<BillIndicators />} />
          </Route>
        </Route>
        {/* Payroll Indicators */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="payrollIndicators" />}>
          <Route path="user" element={<Layout />}>
            <Route path="payrollIndicators" element={<PayrollIndicators />} />
          </Route>
        </Route>
        {/* Rate Master */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="rate" />}>
          <Route path="user" element={<Layout />}>
            <Route path="rate" element={<RateMaster />} />
          </Route>
        </Route>
        {/* Manage company Logo */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="manageCompanyLogo" />}>
          <Route path="user" element={<Layout />}>
            <Route path="manageCompanyLogo" element={<ManageCompanyLogo />} />
          </Route>
        </Route>
        {/* Buisnes Alarm */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="managingBusinesAlarm" />}>
          <Route path="user" element={<Layout />}>
            <Route path="managingBusinesAlarm" element={<BusinessAlarmManager />} />
          </Route>
        </Route>
        {/* Business Dynamics */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="businessDynamics" />}>
          <Route path="user" element={<Layout />}>
            <Route path="businessDynamics/requests" element={<BusinessDynamicsRequests />} />
            <Route
              path="businessDynamics/manage-requests"
              element={<ManageBusinessDynamicsRequests />}
            />
          </Route>
        </Route>
        {/* External Files */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="externalFiles" />}>
          <Route path="user" element={<Layout />}>
            <Route path="externalFiles" element={<ExternalFiles />} />
          </Route>
        </Route>
        {/* Admin Reprocess */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="adminReprocess" />}>
          <Route path="user" element={<Layout />}>
            <Route path="adminReprocess" element={<AdminReprocess />} />
          </Route>
        </Route>
        {/* What If  */}
        <Route element={<ProtectedRoute requiredRole={"user"} path="whatIf" />}>
          <Route path="user" element={<Layout />}>
            <Route path="whatIf" element={<UnifiedWhatIf />} />
            <Route path="whatIf/seeSimulations" element={<ListSimulations />} />
          </Route>
        </Route>
        {/* Not Found */}
        <Route path="*" element={<NotFound />} />
        {/* Loading page */}
        <Route path="/loading" element={<LoadingPage />} />
      </Routes>
    </Suspense>
  );
}
export default AppRoutes;
